import type { Renter } from '@/utils/types/renter.interface';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';

import type { InviteLeaseDetailsFormValues } from '../invite-lease-details.form-values';

// Comma-grouped money string ("1,500") → number. Empty/invalid → undefined.
const parseMoney = (value: string | null | undefined): number | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  const parsed = Number(value.replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

// ISO string → Date. Empty/invalid → undefined.
const parseDate = (value: string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export type MapInviteFormValuesContext = {
  isSmbInvite: boolean;
  isPmi: boolean;
};

/**
 * Bridges the config-native form's engine-shaped values back to the legacy
 * `Renter` shape so the existing submit pipeline (`normalizeInviteLeaseDetailsValues`,
 * `buildInviteLeaseDetailsPayload` / `buildLegacyInviteLeaseDetailsPayload`,
 * controller analytics) is reused unchanged. The form is the only thing that
 * changed; the wire payload contract is preserved exactly.
 *
 * Coverage type derivation: `rentCoverage` / `securityDepositCoverage` are not
 * stored on the form — they're derived from whether the override field has a
 * value at submit. For SMB non-PMI, the months "override" is actually the tier
 * selection from the DDS option-box, so we carve it out and always emit
 * `CompanyDefault` to match the legacy payload. Hidden override fields are
 * cleared by `InviteLeaseDetailsEffects` when their option-box flips to
 * "Company default" (or PMI's customCoverage unchecks), so presence is a
 * faithful signal — the engine doesn't unregister hidden fields the way the
 * legacy form did.
 *
 * Fields the schema guarantees present at submit (address, dates, rent, unit,
 * primary applicant) are cast non-null — the engine only reaches submit after a
 * passing validation, so the `undefined` branch is unreachable in practice.
 */
export const mapInviteFormValuesToRenter = (
  values: InviteLeaseDetailsFormValues,
  ctx: MapInviteFormValuesContext,
): Renter => {
  const months = parseMoney(values.leaseGuaranteeMonthsOverride);
  const sdrAmount = parseMoney(values.securityDepositReplacementAmountOverride);
  // SMB non-PMI uses the months field as a tier picker, not as a customize
  // override — preserve the legacy `CompanyDefault` payload for that variant.
  const derivesCoverageFromOverrides = !ctx.isSmbInvite || ctx.isPmi;

  // Presence — not truthiness — is the signal. `parseMoney` already returns
  // `undefined` for blank / non-numeric input, so `!= null` correctly accepts a
  // user-typed `0` (validation allows it, and "Customize + 0" is a meaningful
  // user choice on the legacy wire).
  const hasMonthsOverride = months != null;
  const hasSdrOverride = sdrAmount != null;

  return {
    address: values.address,
    unit: values.unit ?? '',
    leaseStartDate: parseDate(values.leaseStartDate) as Date,
    leaseEndDate: parseDate(values.leaseEndDate) as Date,
    grossMonthlyRent: parseMoney(values.grossMonthlyRent) as number,
    prepaidRent: parseMoney(values.prepaidRent),
    freeRent: parseMoney(values.freeRent),
    screeningStatus: values.screeningStatus ?? '',
    applicantEmails: (values.applicants ?? []).map(({ email }) => ({ value: email ?? '' })),
    rentCoverage:
      derivesCoverageFromOverrides && hasMonthsOverride
        ? RentCoverageTypes.Customize
        : RentCoverageTypes.CompanyDefault,
    leaseGuaranteeMonthsOverride: months,
    securityDepositCoverage:
      derivesCoverageFromOverrides && hasSdrOverride
        ? SecurityDepositTypes.CustomizeOrRemove
        : SecurityDepositTypes.CompanyDefault,
    securityDepositReplacementAmountOverride: sdrAmount,
    customCoverage: values.customCoverage ?? false,
    includeDepositCoverage: values.includeDepositCoverage ?? false,
    selectedSmbPricing: values.selectedSmbPricing,
    selectedCoverageRuleId: values.selectedCoverageRuleId ?? undefined,
  };
};
